import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb } from 'lucide-react';

interface CardProps {
  content: string;
  isFlipped: boolean;
  isMatched: boolean;
  onClick: () => void;
  onFlag?: () => void;
  onShowHint?: (e: React.MouseEvent, word: string) => void;
  type: 'word' | 'image';
}

const Card: React.FC<CardProps> = ({ content, isFlipped, isMatched, onClick, onFlag, onShowHint, type }) => {
  const cardVariants = {
    flipped: {
      rotateY: 180,
      transition: { duration: 0.3 }
    },
    unflipped: {
      rotateY: 0,
      transition: { duration: 0.3 }
    }
  };

  return (
    <motion.div
      className="w-32 h-32 md:w-40 md:h-40 perspective-1000"
      onClick={!isFlipped && !isMatched ? onClick : undefined}
    >
      <motion.div
        className="relative w-full h-full preserve-3d"
        variants={cardVariants}
        animate={isFlipped || isMatched ? 'flipped' : 'unflipped'}
      >
        {/* Card Front (Hidden) */}
        <div className="absolute top-0 left-0 w-full h-full backface-hidden bg-white rounded-lg shadow-lg flex items-center justify-center p-2 transform rotate-y-180 group">
          {type === 'image' ? (
            <>
              <img referrerPolicy="no-referrer" src={content} alt="card content" className="max-w-full max-h-full object-contain rounded-md" />
              {onFlag && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onFlag();
                  }}
                  title="Report incorrect image"
                  className="absolute top-1 right-1 bg-white/80 p-1.5 rounded-full shadow-sm text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/></svg>
                </button>
              )}
            </>
          ) : (
            <>
              <span className="text-xl font-semibold text-gray-800 text-center">{content}</span>
              {onShowHint && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowHint(e, content);
                  }}
                  title="Show definition (Costs points!)"
                  className="absolute top-1 left-1 bg-white/80 p-1.5 rounded-full shadow-sm text-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-yellow-50"
                >
                  <Lightbulb className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>

        {/* Card Back (Visible) */}
        <div className="absolute top-0 left-0 w-full h-full backface-hidden bg-purple-600 hover:bg-purple-700 rounded-lg shadow-lg flex items-center justify-center cursor-pointer">
          <span className="text-4xl text-white">?</span>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Card;
